import VerdictBadge from "@/components/VerdictBadge";
import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, Loader2, Check, AlertCircle, Languages, Target } from "lucide-react";
import { useFmt } from "@/lib/useFmt";
import { useTranslation } from "react-i18next";
import { strings } from "@/i18n/strings";

const T = strings.ai;

// Verdict-tinted background. Subtle. The panel itself signals deal quality.
const GRADIENT_BY_VERDICT = {
  "Strong Buy": "from-emerald-50 via-white to-indigo-50",
  Buy: "from-emerald-50 via-white to-indigo-50",
  Hold: "from-amber-50 via-white to-indigo-50",
  Risky: "from-orange-50 via-white to-rose-50",
  Reject: "from-rose-50 via-white to-rose-50",
};
const BORDER_BY_VERDICT = {
  "Strong Buy": "border-emerald-200",
  Buy: "border-emerald-200",
  Hold: "border-amber-200",
  Risky: "border-orange-200",
  Reject: "border-rose-200",
};

// Phase 36 Batch AI — chip describing why the suggested offer is what
// it is. Three valid values:
//   - target_return_anchor : derived from the user's target return.
//   - asking_meets_target  : asking already hits the target; no discount.
//   - llm_proposed         : the LLM picked it; basis is its narrative.
const SUGGESTED_OFFER_BASIS_COPY = {
  target_return_anchor: {
    label: "Target-return anchor",
    tone: "bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200",
    hint: "Price needed to hit your target return.",
  },
  asking_meets_target: {
    label: "Asking meets target",
    tone: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
    hint: "Asking price already clears your target return — no discount needed.",
  },
  llm_proposed: {
    label: "AI proposed",
    tone: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
    hint: "AI-derived from comps + cap-rate logic.",
  },
};

export default function AiHeroPanel({ deal, onRun, running }) {
  const ai = deal?.ai_summary;
  const isFallback = ai?.ai_status === "fallback";
  const fmt = useFmt();
  const { t } = useTranslation();

  // Empty state — never analyzed
  if (!ai) {
    return (
      <div
        data-testid="ai-hero-empty"
        className="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-white p-6 shadow-sm sm:p-8"
      >
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
              <Sparkles className="h-3 w-3" /> AI Analyst
            </div>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {T.hero_empty_title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{T.hero_empty_body}</p>
          </div>
          <Button
            data-testid="ai-run-analyst"
            onClick={onRun}
            disabled={running}
            size="lg"
            className="bg-indigo-600 text-white shadow-sm transition-all active:scale-[0.98] hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
          >
            {running ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {T.analyzing}
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" /> {T.run_analysis}
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Populated state
  const agree = ai.verdict === deal.verdict;
  const gradient = GRADIENT_BY_VERDICT[ai.verdict] || "from-indigo-50 via-white to-white";
  const borderCls = BORDER_BY_VERDICT[ai.verdict] || "border-indigo-200";
  // Phase 36 Batch AI — suggested-offer basis chip. Falls back to
  // "llm_proposed" when the backend hasn't tagged it (older payloads).
  const offerBasis = ai.suggested_offer_basis || "llm_proposed";
  const basisCopy = SUGGESTED_OFFER_BASIS_COPY[offerBasis] || SUGGESTED_OFFER_BASIS_COPY.llm_proposed;

  return (
    <div
      data-testid="ai-hero-panel"
      className={`relative overflow-hidden rounded-2xl border ${borderCls} bg-gradient-to-br ${gradient} p-6 shadow-sm sm:p-8`}
    >
      <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/40 blur-3xl" aria-hidden="true" />

      <div className="relative flex flex-wrap items-start justify-between gap-3">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
          <Sparkles className="h-3 w-3" /> AI Verdict
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onRun}
            disabled={running}
            data-testid="ai-translate-analysis"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 transition-colors hover:text-indigo-700 disabled:opacity-50"
            title={t("ai.translate_analysis")}
          >
            <Languages className="h-3 w-3" />
            {t("ai.translate_analysis")}
          </button>
          <button
            type="button"
            onClick={onRun}
            disabled={running}
            data-testid="ai-regenerate-link"
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 transition-colors hover:text-indigo-900 disabled:opacity-50"
          >
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {running ? T.regenerating : t("ai.regenerate")}
          </button>
        </div>
      </div>

      <div className="relative mt-5 grid items-center gap-6 lg:grid-cols-[1fr_auto]">
        <div className="min-w-0">
          <VerdictBadge verdict={ai.verdict} size="lg" />
          <h2
            className="mt-3 text-2xl font-bold leading-tight tracking-tight text-slate-900 sm:text-3xl"
            data-testid="ai-headline"
          >
            {ai.headline}
          </h2>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-700">{ai.main_reason}</p>

          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
            <span
              data-testid="ai-vs-engine"
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium ${
                agree ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
              }`}
            >
              {agree ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
              {agree ? T.agree_engine : T.disagree_engine}
            </span>
            {isFallback && (
              <span
                data-testid="ai-fallback-badge"
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-800"
              >
                <AlertCircle className="h-3 w-3" /> {T.fallback_badge}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-stretch gap-3 rounded-2xl border border-white/60 bg-white/70 p-5 shadow-sm backdrop-blur-sm sm:min-w-[220px]">
          {ai.suggested_offer_price ? (
            <div className="w-full text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <Target className="h-3 w-3" /> {T.suggested_offer}
              </div>
              <div className="mt-1 text-2xl font-bold text-indigo-600 tabular-nums" data-testid="ai-suggested-offer">
                {fmt.money(ai.suggested_offer_price)}
              </div>
              <div className="mt-2 flex flex-col items-center gap-1">
                <span
                  data-testid="ai-suggested-offer-basis"
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${basisCopy.tone}`}
                >
                  {basisCopy.label}
                </span>
                <p className="px-1 text-[11px] leading-snug text-slate-500">
                  {basisCopy.hint}
                </p>
              </div>
            </div>
          ) : (
            <div className="w-full text-center text-xs text-slate-500">
              Run analysis to surface a buyer offer recommendation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
